import {styled} from '@mui/material/styles';
import {forwardRef} from 'react';
import {Box} from '../Box';
import {Button} from '../Button';
import {Typography} from '../Typography';
import {useFileInputContext} from './FileInputContext';
import {FileInputGuardsPanel} from './subComponents/FileInputGuardsPanel';
import {FileInputOverlay} from './subComponents/FileInputOverlay';
import {FileInputValidationsPanel} from './subComponents/FileInputValidationsPanel';
import {FileList} from './subComponents/FileList';
import {StyledFileInput} from './subComponents/StyledFileInput';
import {FileInputProps} from './types/FileInputProps';

const StyledWrapper = styled('div')(({theme}) => ({
    display: 'flex',
    flexWrap: 'wrap',
    [theme.breakpoints.up('md')]: {
        flexWrap: 'nowrap'
    }
}));

const StyledPanel = styled(Box)(({theme}) => ({
    padding: `${theme.tokens.FuelSpacing4} 0rem ${theme.tokens.FuelSpacing4} 0rem`,
    width: '100%',
    [theme.breakpoints.up('md')]: {
        padding: `${theme.tokens.FuelSpacing2} ${theme.tokens.FuelSpacing2} ${theme.tokens.FuelSpacing2} ${theme.tokens.FuelSpacing4}`,
        maxWidth: '15rem'
    }
}));

const StyledActionBox = styled('div')({
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '-0.25rem -0.375rem'
});

const StyledActionItem = styled('div')({
    padding: '0.25rem 0.375rem'
});

export const FileInput = forwardRef<HTMLDivElement, FileInputProps>((_, ref) => {
    // automatically hook into context
    const {
        id,
        name,
        DisplayIcon,
        openFileDialog,
        disabled,
        error,
        files,
        rejectedFiles,
        getRootProps,
        getInputProps,
        validators,
        hideValidationsPanel,
        isDragActive,
        inputRef,
        textMap,
        ValidationPanelProps,
        handleClearFiles,
        maxFiles
    } = useFileInputContext();

    return (
        <StyledWrapper>
            <StyledFileInput
                {...getRootProps({
                    className: 'FileInput-root'
                })}
                disabled={disabled}
                error={error}
                ref={ref}
            >
                <div
                    className='FileInput-content'
                    style={{
                        pointerEvents: isDragActive ? 'none' : 'auto'
                    }}
                >
                    <FileInputOverlay/>
                    <div className='FileInput-information'>
                        {files.length > 0 ? (
                            <FileList/>
                        ) : (
                            <DisplayIcon
                                aria-label='Upload'
                                className='FileInput-icon'
                            />
                        )}
                    </div>
                    <input
                        {...getInputProps({
                            id,
                            name: name || 'fileInputField'
                        })}
                        ref={inputRef}
                    />
                    <StyledActionBox className='FileInput-actionBox'>
                        <StyledActionItem className='FileInput-actionItem'>
                            <Button
                                disabled={disabled || !(files.length < maxFiles)}
                                onClick={(): void => {
                                    openFileDialog();
                                }}
                                type='button'
                                variant={
                                    files.length > 0 || rejectedFiles.length > 0 ?
                                        'primary' :
                                        'secondary'
                                }
                            >
                                {files.length > 0 ? textMap.actionAdd : textMap.actionChoose}
                            </Button>
                        </StyledActionItem>
                        <StyledActionItem className='FileInput-actionItem'>
                            {files.length > 0 || rejectedFiles.length > 0 ? (
                                <Button
                                    disabled={disabled}
                                    onClick={(): void => {
                                        handleClearFiles();
                                    }}
                                    type='button'
                                >
                                    {textMap.actionClear}
                                </Button>
                            ) : (
                                <Typography
                                    color={disabled ? 'tertiary' : 'initial'}
                                    sx={{
                                        opacity: disabled ? 0.75 : 1
                                    }}
                                    variant='body1'
                                >
                                    {textMap.actionSupplement}
                                </Typography>
                            )}
                        </StyledActionItem>
                    </StyledActionBox>
                </div>
            </StyledFileInput>
            {!hideValidationsPanel && (
                <StyledPanel
                    as='aside'
                    {...ValidationPanelProps}
                >
                    <FileInputGuardsPanel/>
                    {Object.keys(validators).length > 0 && <FileInputValidationsPanel/>}
                </StyledPanel>
            )}
        </StyledWrapper>
    );
});

FileInput.displayName = 'FileInput';
